import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getPayrollSummaries } from '../../../services/timesheetService';
import formatMinutes from '../../../utils/formatMinutes';
import PersonCell from '../../../components/ui/PersonCell';
import { useTaFilterColumns } from '../../pdks-reports/config/taFilterColumns';

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $bg }) => $bg || '#9e9e9e15'};
  color: ${({ $color }) => $color || '#9e9e9e'};
`;

export const statusColors = {
  draft: { bg: '#9e9e9e15', text: '#9e9e9e' },
  confirmed: { bg: '#4caf5015', text: '#4caf50' },
  locked: { bg: '#2196f315', text: '#2196f3' },
};

// Ondalık iş günü (AKM POC §5.4 — yarım gün izin / arife 0,5 sayılır): dilin ondalık ayırıcısıyla, en çok
// iki basamak; tam sayı gereksiz ",00" taşımaz.
export const formatWorkDays = (value, lang) => {
  if (value === null || value === undefined || value === '') return '-';
  const n = Number(value);
  if (Number.isNaN(n)) return '-';
  return n.toLocaleString(lang || 'tr', { maximumFractionDigits: 2 });
};

const usePayrollApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n?.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    getPayrollSummaries(paramsToUse)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .catch(() => {
        setData([]);
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => {
    setRefreshTrigger((p) => p + 1);
  }, []);

  const statusLabels = useMemo(() => ({
    draft: t('payroll.draft'),
    confirmed: t('payroll.confirmed'),
    locked: t('payroll.locked'),
  }), [t]);
  // AKM POC §7.5 — ortak PDKS süzgeç seti (statü, grup, departman, lokasyon, vardiya, kişi); tabloda görünmez.
  const filterColumns = useTaFilterColumns(t);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'userFullName', label: t('payroll.employee'),
      options: {
        filter: true, sort: true,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <PersonCell name={v} profilePicture={row?.userProfilePicture} />;
        },
      },
    },
    {
      name: 'year', label: t('payroll.year'),
      options: {
        filter: true, sort: true,
        filterType: 'textField',
        filterOptions: { defaultOp: 'eq' },
      },
    },
    {
      name: 'month', label: t('payroll.month'),
      options: {
        filter: true, sort: true,
        filterType: 'textField',
        filterOptions: { defaultOp: 'eq' },
      },
    },
    {
      name: 'scheduledWorkDays', label: t('payroll.scheduledWorkDays'),
      options: { filter: false, sort: false },
    },
    {
      name: 'actualWorkedDays', label: t('payroll.actualWorkedDays'),
      options: { filter: false, sort: false },
    },
    {
      name: 'absentDays', label: t('payroll.absentDays'),
      options: { filter: false, sort: false },
    },
    {
      // AKM POC §5.4 — ücretli izin İŞ GÜNÜYLE sayılır (takvim günü değil; yarım gün 0,5).
      name: 'paidLeaveWorkDays', label: t('payroll.paidLeaveDays'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatWorkDays(v, lang) },
    },
    {
      name: 'scheduledMinutes', label: t('payroll.scheduledMinutes'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => formatMinutes(v),
      },
    },
    {
      name: 'actualWorkedMinutes', label: t('payroll.actualWorkedMinutes'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => formatMinutes(v),
      },
    },
    {
      // Artık ONAYLANAN fazla mesai (hesaplanan fazla mesainin onaylanan kısmı + önceden onaylı). Etiket
      // "(Dk)" taşır → değer dakika olarak basılır.
      name: 'totalOvertimeMinutes', label: t('payroll.approvedOvertimeMinutes'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (Number.isFinite(v) ? v : 0),
      },
    },
    {
      // AKM POC §7.6 — hesaplanan (ölçülen) fazla mesai: onaylanandan ayrı sütun (bordroya giren onaylanandır).
      name: 'totalComputedOvertimeMinutes', label: t('payroll.computedOvertimeMinutes'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
    },
    {
      name: 'totalWeeklyRestWorkMinutes', label: t('payroll.weeklyRestWorkMinutes'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
    },
    {
      name: 'totalHolidayWorkMinutes', label: t('payroll.holidayWorkMinutes'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
    },
    {
      name: 'nightMinutes', label: t('payroll.nightMinutes'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
    },
    {
      name: 'hourlyLeaveMinutes', label: t('payroll.hourlyLeaveMinutes'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
    },
    {
      name: 'totalUndertimeMinutes', label: t('payroll.undertimeMinutes'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
    },
    {
      name: 'totalOutsideMinutes', label: t('payroll.totalOutside'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => formatMinutes(v),
      },
    },
    {
      name: 'lateMinutes', label: t('payroll.lateMinutes'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => formatMinutes(v),
      },
    },
    {
      name: 'status', label: t('payroll.status'),
      options: {
        filter: true, sort: true,
        filterType: 'dropdown',
        // Süzgeç KOD gönderir (status[eq]=draft) — sunucu küçük harf kodla eşler; seçenekte ve rozette etiket görünür
        // (MovementRecordsPage deseni). Eskiden etiket ("Taslak") gidiyordu ve süzgeç hiçbir satır bulmuyordu.
        filterOptions: {
          defaultOp: 'eq',
          names: Object.keys(statusColors),
          renderValue: (v) => statusLabels[v] || v,
        },
        filterList: [],
        customFilterListOptions: { render: (v) => statusLabels[v] || v },
        customBodyRender: (v) => {
          const colors = statusColors[v] || { bg: '#9e9e9e15', text: '#9e9e9e' };
          return <StatusBadge $bg={colors.bg} $color={colors.text}>{statusLabels[v] || v}</StatusBadge>;
        },
      },
    },
    ...filterColumns,
  ], [t, lang, data, statusLabels, filterColumns]);

  return { columns, data, fetching, pagination, refresh, statusLabels };
};

export { StatusBadge };
export default usePayrollApi;
